
export interface OpenRouterModel {
  id: string;
  name: string;
  pricing: {
    prompt: string;
    completion: string;
  };
  context_length: number;
}

export interface OpenRouterHealthResult {
  latency: number;
  status?: number;
  error?: string;
}

export const OPENROUTER_FREE_MODEL_SHORTLIST = [
  'openrouter/free',
  'openai/gpt-oss-120b:free',
  'qwen/qwen3-next-80b-a3b-instruct:free',
  'nvidia/nemotron-3-super-120b-a12b:free',
  'google/gemma-4-31b-it:free',
];

export class OpenRouterService {
  private static API_URL = 'https://openrouter.ai/api/v1';
  static readonly AUTO_MODEL_ID = 'openrouter/free';
  static readonly AUTO_MODEL_NAME = 'OpenRouter Free Router';
  static readonly PAID_AUTO_MODEL_ID = 'openrouter/auto';
  static readonly PAID_AUTO_MODEL_NAME = 'OpenRouter Auto';
  static readonly FREE_MODEL_SHORTLIST = OPENROUTER_FREE_MODEL_SHORTLIST;

  static isFreeModel(model?: Pick<OpenRouterModel, 'id' | 'pricing'> | string): boolean {
    if (!model) return false;
    if (typeof model === 'string') {
      return model === this.AUTO_MODEL_ID || model.endsWith(':free');
    }

    return (
      parseFloat(model.pricing?.prompt || '0') === 0 &&
      parseFloat(model.pricing?.completion || '0') === 0
    );
  }

  static getFreeFirstFallbacks(modelId?: string): string[] {
    const requested = (modelId || '').trim();
    const ordered = [
      requested && this.isFreeModel(requested) ? requested : '',
      ...OPENROUTER_FREE_MODEL_SHORTLIST,
    ].filter(Boolean);

    return Array.from(new Set(ordered));
  }

  private static async readErrorMessage(response: Response): Promise<string> {
    try {
      const payload = await response.json();
      return (
        payload?.error?.message ||
        payload?.message ||
        payload?.detail ||
        ''
      );
    } catch {
      return '';
    }
  }

  static async fetchModels(apiKey: string): Promise<{ free: OpenRouterModel[], paid: OpenRouterModel[] }> {
    try {
      const response = await fetch(`${this.API_URL}/models`, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'HTTP-Referer': window.location.origin,
          'X-Title': 'BDAT-Autonomous'
        }
      });
      const data = await response.json();
      const models: OpenRouterModel[] = Array.isArray(data.data) ? data.data : [];

      const uniqueModels = models.filter((model, index, all) => all.findIndex((candidate) => candidate.id === model.id) === index);
      const free = uniqueModels
        .filter((m) => this.isFreeModel(m))
        .sort((a, b) => b.context_length - a.context_length);
      const paid = uniqueModels
        .filter((m) => !this.isFreeModel(m))
        .sort((a, b) => parseFloat(a.pricing.prompt) - parseFloat(b.pricing.prompt));

      return { free, paid };
    } catch (error) {
      console.error('Error fetching OpenRouter models:', error);
      return { free: [], paid: [] };
    }
  }

  static async checkHealthDetailed(apiKey: string, modelId: string): Promise<OpenRouterHealthResult> {
    if (!apiKey?.trim()) {
      return { latency: -1, error: 'OpenRouter API key ontbreekt.' };
    }

    const start = performance.now();
    try {
      const response = await fetch(`${this.API_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'BDAT-Autonomous'
        },
        body: JSON.stringify(this.buildRequestBody(modelId || this.AUTO_MODEL_ID, 'Return exactly: ok', 'You are a health check endpoint.', 4))
      });
      if (response.ok) {
        return { latency: Math.round(performance.now() - start), status: response.status };
      }

      const detail = await this.readErrorMessage(response);
      return {
        latency: -1,
        status: response.status,
        error: detail || `OpenRouter request failed (${response.status})`,
      };
    } catch (error: any) {
      return { latency: -1, error: error?.message || 'OpenRouter niet bereikbaar.' };
    }
  }

  static async checkHealth(apiKey: string, modelId: string): Promise<number> {
    const result = await this.checkHealthDetailed(apiKey, modelId);
    return result.latency;
  }

  private static buildRequestBody(modelId: string, prompt: string, context: string, maxTokens?: number) {
    return {
      model: modelId,
      messages: [
        { role: 'system', content: context },
        { role: 'user', content: prompt }
      ],
      provider: {
        sort: 'price',
      },
      ...(maxTokens ? { max_tokens: maxTokens } : {}),
    };
  }

  static async generateText(apiKey: string, prompt: string, context: string, modelId: string): Promise<string> {
    try {
      const response = await fetch(`${this.API_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'BDAT-Autonomous'
        },
        body: JSON.stringify(this.buildRequestBody(modelId, prompt, context))
      });
      if (!response.ok) {
        const detail = await this.readErrorMessage(response);
        const suffix = detail ? ` - ${detail}` : '';
        throw new Error(`OpenRouter request failed (${response.status})${suffix}`);
      }

      const data = await response.json();
      const content = data.choices?.[0]?.message?.content || "";

      if (!content.trim()) {
        throw new Error(`OpenRouter gaf geen bruikbare output terug voor model ${modelId}.`);
      }

      return content;
    } catch (error) {
      console.error('OpenRouter Error:', error);
      throw error;
    }
  }

  static async generateTextFreeFirst(apiKey: string, prompt: string, context: string, modelId?: string): Promise<{ text: string; modelId: string }> {
    let lastError: unknown = null;

    for (const candidate of this.getFreeFirstFallbacks(modelId)) {
      try {
        const text = await this.generateText(apiKey, prompt, context, candidate);
        return { text, modelId: candidate };
      } catch (error) {
        lastError = error;
        console.warn(`[OpenRouter] Free model failed, trying next: ${candidate}`, error);
      }
    }

    throw lastError instanceof Error ? lastError : new Error('Geen gratis OpenRouter model gaf bruikbare output terug.');
  }
}
